import type { Web3DViewer } from '@technology/web3d';
import {
  GenericLine,
  LineEditingModel,
  Point3D,
  type ICADObject,
} from '@technology/web3d-plugin-line-editing';
import { CAD_FEATURE_ID_KEY } from '@trimble/geospatial';
import type {
  CadObjectDebugSnapshot,
  SceneVertexTuple,
} from './cad-object-debug-snapshot.types';
import { CAD_EDITING_MODEL_ID } from './feature-extraction-cad-config';

// Selection falls back to the CAD guid for unsaved features, so match both.
export function findCadObjectDebugSnapshot(
  featureId: string
): CadObjectDebugSnapshot | undefined {
  const viewers = Array.from(
    document.querySelectorAll<Web3DViewer>('web3d-viewer')
  );

  for (const [paneIndex, viewer] of viewers.entries()) {
    const model = viewer.getModel(CAD_EDITING_MODEL_ID, LineEditingModel);
    if (!model) {
      continue;
    }
    const entity = model
      .allEntities()
      .find(
        (item) =>
          item.metaData[CAD_FEATURE_ID_KEY] === featureId ||
          item.guid === featureId
      );
    return entity ? toSnapshot(paneIndex, entity) : undefined;
  }
  return undefined;
}

function toSnapshot(
  paneIndex: number,
  entity: ICADObject
): CadObjectDebugSnapshot {
  return {
    paneIndex,
    guid: entity.guid,
    id: entity.id,
    type: entity.constructor.name,
    sceneVertices: sceneVerticesOf(entity),
    editable: entity.editable,
    style: entity.style,
    metaData: { ...entity.metaData },
  };
}

function sceneVerticesOf(entity: ICADObject): SceneVertexTuple[] {
  if (entity instanceof GenericLine) {
    return entity.getPoints().map((p) => [p.x, p.y, p.z]);
  }
  if (entity instanceof Point3D) {
    return [[entity.position.x, entity.position.y, entity.position.z]];
  }
  return [];
}

// CAD styles and metadata can hold back-references, which JSON.stringify throws on.
export function stringifyDebugJson(value: unknown, indent: number): string {
  const seen = new WeakSet<object>();
  return JSON.stringify(
    value,
    (_key, current: unknown) => {
      if (typeof current === 'object' && current !== null) {
        if (seen.has(current)) {
          return '[Repeated reference]';
        }
        seen.add(current);
      }
      return current;
    },
    indent
  );
}
